// KWF Admin sidebar — ported to the KWF Admin design artifact.
// Dark teal rail with Foundation / Platform / Pharmacy sections,
// user card + logout at the bottom.
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleAuth } from "../Redux/Slices/auth-slice";
import { fetchApi } from "../utlis/axios";
import { Icon } from "./admin/_adminApi";

// Group definitions. Keys match the previous role filter — "admin" are the
// KWF foundation operator pages, "pharmacist" are the dispensing shop pages,
// "staff" are Django is_staff IMS admin pages.
const GROUPS = [
  {
    label: "Foundation",
    role: "admin",
    items: [
      { href: "/dashboard_graph",    name: "Dashboard",       icon: Icon.apps },
      { href: "/applications",       name: "Applications",    icon: Icon.file },
      { href: "/program",            name: "Programs",        icon: Icon.package },
      { href: "/familyProgram",      name: "Sub-Programs",    icon: Icon.activity },
      { href: "/family_donation",    name: "Family Donation", icon: Icon.users },
      { href: "/kwf_donation",       name: "Donation",        icon: Icon.wallet },
      { href: "/kwf_medical_staff",  name: "Medical Stores",  icon: Icon.hospital },
    ],
  },
  {
    label: "Pharmacy",
    role: "pharmacist",
    items: [
      { href: "/verify_fingerprint", name: "Verify Fingerprint", icon: Icon.shield },
      { href: "/kwf_receipts",       name: "Receipts",           icon: Icon.file },
      { href: "/kwf_monthlyreport",  name: "Monthly Report",     icon: Icon.activity },
    ],
  },
  {
    label: "Platform",
    staffOnly: true,
    items: [
      { href: "/admin/app",      name: "App",   icon: Icon.apps },
      { href: "/admin/licences", name: "IMS",   icon: Icon.hospital },
      { href: "/admin/users",    name: "Users", icon: Icon.users },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role, first_name, last_name, username, is_superuser, is_staff: userIsStaff } =
    useSelector((s) => s.authSlice.userInfo?.user || {});
  const { access } = useSelector((s) => s.authSlice.userInfo || {});
  const [isStaff, setIsStaff] = useState(!!userIsStaff);

  useEffect(() => {
    if (!access) return;
    fetchApi.get("admin/me/", { headers: { Authorization: `Bearer ${access}` } })
      .then((r) => setIsStaff(!!r?.data?.is_staff))
      .catch(() => {});
  }, [access]);

  const logout = () => {
    dispatch(toggleAuth({ isLogin: false, userInfo: null }));
    navigate("/");
    localStorage.removeItem("activeTab");
  };

  const visibleGroups = GROUPS.filter((g) => (g.staffOnly ? isStaff : g.role === role));

  const fullName = [first_name, last_name].filter(Boolean).join(" ") || username || "User";
  const initials = fullName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "U";
  const subtitle = is_superuser ? "Superuser" : isStaff ? "Staff" : (role || "User");

  const isActive = (href) => {
    const p = location.pathname;
    // Match sidebar routes robustly: dashboard uses /dashboard prefix,
    // and nested detail pages (e.g. /dashboard/admin/licences/:id) should
    // still mark the parent "IMS" link active.
    return p === `/dashboard${href}` || p.startsWith(`/dashboard${href}/`);
  };

  return (
    <aside className="ad-side">
      <div className="ad-brand">
        <div className="ad-brand-mark"><Icon.cross /></div>
        <div className="ad-brand-t">
          <strong>KWF</strong>
          <span>Khadim Welfare Foundation</span>
        </div>
      </div>
      <nav className="ad-nav">
        {visibleGroups.map((g) => (
          <React.Fragment key={g.label}>
            <div className="ad-sec">{g.label}</div>
            {g.items.map((it) => {
              const I = it.icon;
              return (
                <Link
                  key={it.href}
                  to={`/dashboard${it.href}`}
                  className={`ad-link${isActive(it.href) ? " active" : ""}`}
                >
                  <I />
                  <span>{it.name}</span>
                </Link>
              );
            })}
          </React.Fragment>
        ))}
      </nav>
      <div className="ad-side-foot">
        <div className="ad-me">
          <div className="ad-me-av">{initials}</div>
          <div className="ad-me-t">
            <strong>{fullName}</strong>
            <span>{subtitle}</span>
          </div>
          <button className="ad-me-out" onClick={logout} title="Logout"><Icon.logOut /></button>
        </div>
      </div>
    </aside>
  );
}
